import React from 'react';
import { useTranslation } from 'react-i18next';

import { isElalCompany, isIsracardCompany } from '@/utils/isCompany';
import { ReactComponent as OpenIcon } from '@/img/results_page/open.svg';
import { ReactComponent as ExternalLinkIcon } from '@/img/results_page/external-link.svg';

import Rm3PhotoCarousel from './Rm3PhotoCarousel';
import useRm3Photos from './useRm3Photos';
import Rm3RoomSpecs from './Rm3RoomSpecs';
import Rm3AmenityChips from './Rm3AmenityChips';
import Rm3DealRow from './Rm3DealRow';
import { IconChevron } from './Rm3Icons';

// Spec-v2 §3.1 (A17): the room panel sticks while its deals scroll when the room shows more than this many.
const STICKY_PANEL_MAX_DEALS = 4;

/** The "More details" link icon, as today's room-details link: Elal `OpenIcon`, Isracard `ExternalLinkIcon`. */
function MoreDetailsIcon() {
    if (isElalCompany()) {
        return <OpenIcon />;
    }
    if (isIsracardCompany()) {
        return <ExternalLinkIcon />;
    }
    return <IconChevron size={16} className="rm3-chevron" />;
}

/**
 * One RM3 container (= a display group, the design's "room"): media, name, specs and amenities on the room
 * panel, then ONE flat deal list with no card headings (spec-v2 §1.1). Each row keeps its card's title (Book
 * name) and bed line. `meta` was computed on the unfiltered containers. > 1000 px one row; 701–1000 px stacked.
 * The container has no price (spec §3.0).
 */
function Rm3RoomContainer({ container, deals, meta, ribbons, displayOrder, context, onOpenDetails }) {
    const { t } = useTranslation();
    const { details, label } = container;
    // Spec P2.1: the modal opens at the photo shown, over the same working list (photos found broken stay dropped).
    const photos = useRm3Photos(details.images);
    const openDetails = details.room ? () => onOpenDetails(photos.index, photos.broken, photos.markBroken) : null;

    return (
        <div
            className="rm3-room"
            data-testid="rm3-room"
            data-display-group-key={container.key}
            data-tall={deals.length > STICKY_PANEL_MAX_DEALS}
        >
            <div className="rm3-room__panel">
                <div className="rm3-room__panel-inner">
                    <Rm3PhotoCarousel
                        images={photos.photos}
                        alt={label}
                        onOpen={openDetails}
                        startIndex={photos.index}
                        onSlide={photos.setIndex}
                        onImageError={photos.markBroken}
                    />
                    <div className="rm3-room__info">
                        <div className="rm3-room__heading">
                            <div className="rm3-room__name" data-testid="rm3-room-title">
                                <bdi>{label}</bdi>
                            </div>
                            <Rm3RoomSpecs
                                details={details}
                                bedText={meta.multiCard ? null : meta.singleBedText}
                                bedKind={meta.singleBedKind}
                            />
                        </div>
                        {(details.amenities.length > 0 || openDetails) && (
                            <div className="rm3-room__amenities">
                                <Rm3AmenityChips amenities={details.amenities} />
                                {openDetails && (
                                    <button
                                        type="button"
                                        className="rm3-more-details"
                                        onClick={openDetails}
                                        data-testid="rm3-room-more-details"
                                    >
                                        {t('HotelDealsRm3.MoreDetails')}
                                        <MoreDetailsIcon />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="rm3-room__deals">
                <div className="rm3-room__list">
                    {deals.map((deal) => {
                        const cardId = meta.cardIdByDealId.get(deal.id);
                        const cardMeta = meta.cards.get(cardId) || {};
                        return (
                            <Rm3DealRow
                                key={deal.id}
                                deal={deal}
                                ribbon={ribbons.get(deal.id)}
                                roomName={cardMeta.title}
                                cardId={cardId}
                                bedText={cardMeta.bedText}
                                bedKind={cardMeta.bedKind}
                                displayedOrder={displayOrder.get(deal.id)}
                                context={context}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default Rm3RoomContainer;
